import { useState } from 'react';

import { fetchPutCreditCardDocumentsSign } from '@iwoca/lapi-client/edge';
import { Button, Text } from '@iwoca/orion';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';

import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import { SafeHTML } from '../../../../ui/elements/SafeHTML/SafeHTML';
import { ScrollPanel } from '../../../../ui/scrollPanel/ScrollPanel';
import { Document } from '../types';
import { useFetchCreditCardDocumentContent } from '../useCreditCardDocumentContent';

export const DocumentViewer = ({
    document,
    setSelectedDocument
}: {
    document: Document;
    setSelectedDocument: (document: Document | null) => void;
}) => {
    const { data: documentHtml } = useFetchCreditCardDocumentContent({
        documentId: document.id,
        signingState: 'unsigned',
        fileFormat: 'html'
    });

    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

    const stateKey = useStateKey();
    const queryClient = useQueryClient();

    const { mutate: handleSignDocument, isLoading: isSigningDocument } =
        useMutation({
            mutationFn: () =>
                fetchPutCreditCardDocumentsSign({
                    stateKey,
                    documentId: document.id
                }),
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: ['credit-card-documents-metadata', stateKey]
                });
                setSelectedDocument(null);
            }
        });

    return (
        <div className="mx-auto max-w-3xl px-l">
            <div className="my-2xl">
                <h1 className="mb-m text-3xl font-bold">
                    <FormattedMessage
                        id="_.credit_card.onboarding.document_viewer.title"
                        defaultMessage="Your iwoca {documentType}"
                        values={{
                            documentType: document.title.toLowerCase()
                        }}
                    />
                </h1>

                <Text className="text-xl">
                    <FormattedMessage
                        id="_.credit_card.onboarding.document_viewer.description"
                        defaultMessage="Please read the agreement below and sign using the button."
                    />
                </Text>
            </div>

            <div className="mb-l rounded-s border border-solid border-secondary-90 bg-white">
                <ScrollPanel
                    type="article"
                    enabled={true}
                    onBottomVisible={() => {
                        setHasScrolledToBottom(true);
                    }}
                    className="sm:max-h-[400px] sm:min-h-[350px] max-h-[450px] min-h-[400px] overflow-y-auto p-l"
                    data-testid="document-scroll-panel"
                >
                    <SafeHTML
                        html={documentHtml as string}
                        className="prose max-w-none text-m"
                    />
                </ScrollPanel>
            </div>

            <div className="flex gap-m">
                <Button
                    onClick={() => setSelectedDocument(null)}
                    variant="secondary"
                >
                    <FormattedMessage
                        id="_.credit_card.onboarding.document_viewer.cancel_button"
                        defaultMessage="Cancel"
                    />
                </Button>
                <Button
                    onClick={() => handleSignDocument()}
                    variant="primary"
                    disabled={!hasScrolledToBottom || isSigningDocument}
                >
                    <FormattedMessage
                        id="_.credit_card.onboarding.document_viewer.confirm_button"
                        defaultMessage="Confirm and sign"
                    />
                </Button>
            </div>
        </div>
    );
};
